import { useEffect, useState } from 'react';
import { Alert, Button } from './ui.js';
import { useProjectNotes, useSaveProjectNotes } from '../api/notes.js';

/**
 * Per-project staff notepad: a plain, unformatted scratchpad for this project
 * that every staff member can read and edit. Founders never see it.
 */
export function NotesPanel({ projectId }: { projectId: string }) {
  const notes = useProjectNotes(projectId);
  const save = useSaveProjectNotes(projectId);
  const [content, setContent] = useState('');
  const [loaded, setLoaded] = useState(false);

  // Seed the textarea once from the server, then leave the user's edits alone.
  useEffect(() => {
    if (notes.isSuccess && !loaded) {
      setContent(notes.data.content);
      setLoaded(true);
    }
  }, [notes.isSuccess, notes.data, loaded]);

  const dirty = loaded && content !== (notes.data?.content ?? '');

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-end">
        <Button disabled={!dirty || save.isPending} onClick={() => save.mutate(content)}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {notes.isError ? <Alert>Could not load the notes.</Alert> : null}
      {save.isError ? <Alert>Could not save. Please try again.</Alert> : null}

      <textarea
        className="min-h-[20rem] w-full rounded-md border border-secondary/20 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        placeholder={notes.isPending ? 'Loading…' : 'Notes about this project…'}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        disabled={notes.isPending}
      />

      <p className="text-xs opacity-60">
        {dirty ? 'Unsaved changes. ' : save.isSuccess ? 'Saved. ' : ''}
        {notes.data?.updatedAt
          ? `Last edited ${new Date(notes.data.updatedAt).toLocaleString()}${
              notes.data.updatedByEmail ? ` by ${notes.data.updatedByEmail}` : ''
            }.`
          : 'Not saved yet.'}
      </p>
    </div>
  );
}
